import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { DEFAULT_COLUMNS } from '../utils/columnConfig';

export interface ColumnConfig {
  id: string;
  label: string;
  visible: boolean;
  locked?: boolean; // For columns that should always be visible (Status, Last Edited)
  width?: number; // Persisted column width in pixels (resizable columns)
}

interface ColumnSettingsProps {
  columns: ColumnConfig[];
  onSave: (columns: ColumnConfig[]) => void;
  onClose: () => void;
}

export function ColumnSettings({ columns, onSave, onClose }: ColumnSettingsProps) {
  const [localColumns, setLocalColumns] = useState<ColumnConfig[]>(columns);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [focusedIndex, setFocusedIndex] = useState<number>(0);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Sync with external column changes (from drag-and-drop in main table)
  useEffect(() => {
    setLocalColumns(columns);
  }, [columns]);

  // Scroll focused item into view
  useEffect(() => {
    const focusedElement = itemRefs.current[focusedIndex];
    if (focusedElement) {
      focusedElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [focusedIndex]);

  // Define toggleColumn before the keyboard handler that uses it
  const toggleColumn = useCallback((id: string) => {
    setLocalColumns((prev) =>
      prev.map((col) => (col.id === id && !col.locked ? { ...col, visible: !col.visible } : col)),
    );
  }, []);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex((prev) => Math.min(prev + 1, localColumns.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Enter':
        case ' ': {
          e.preventDefault();
          const column = localColumns[focusedIndex];
          if (column && !column.locked) {
            toggleColumn(column.id);
          }
          break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [localColumns, focusedIndex, onClose, toggleColumn]);

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    // Only update visual indicator during drag
    setDragOverIndex(index);
  };

  const handleDrop = (index: number) => {
    if (draggedIndex === null || draggedIndex === index) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    // Perform actual reorder on drop
    const newColumns = [...localColumns];
    const draggedColumn = newColumns[draggedIndex];

    // Remove from old position
    newColumns.splice(draggedIndex, 1);
    // Insert at new position
    newColumns.splice(index, 0, draggedColumn);

    setLocalColumns(newColumns);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const moveColumnUp = (index: number) => {
    if (index === 0) return;
    const newColumns = [...localColumns];
    [newColumns[index - 1], newColumns[index]] = [newColumns[index], newColumns[index - 1]];
    setLocalColumns(newColumns);
  };

  const moveColumnDown = (index: number) => {
    if (index === localColumns.length - 1) return;
    const newColumns = [...localColumns];
    [newColumns[index], newColumns[index + 1]] = [newColumns[index + 1], newColumns[index]];
    setLocalColumns(newColumns);
  };

  const handleSave = () => {
    onSave(localColumns);
    onClose();
  };

  const handleReset = () => {
    const resetColumns = DEFAULT_COLUMNS.map((col) => ({
      id: col.id,
      label: col.label,
      visible: col.visible,
      locked: col.locked,
      width: col.width,
    }));
    setLocalColumns(resetColumns);
  };

  const visibleCount = localColumns.filter((col) => col.visible).length;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div
        className="w-[500px] bg-white rounded-xl border border-slate-200 shadow-xl overflow-hidden flex flex-col max-h-[80vh]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="column-settings-title"
      >
        {/* Header */}
        <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between flex-shrink-0">
          <div>
            <p
              id="column-settings-title"
              className="text-xs font-semibold uppercase tracking-wider text-slate-600"
            >
              Customize Columns
            </p>
            <p className="text-[10px] text-slate-400 mt-0.5">
              Drag to reorder, toggle to show/hide
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-1.5">
            {localColumns.map((column, index) => (
              <div
                key={column.id}
                ref={(el) => (itemRefs.current[index] = el)}
                draggable={!column.locked}
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={() => handleDrop(index)}
                onDragEnd={handleDragEnd}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg border transition-all ${
                  focusedIndex === index ? 'ring-2 ring-[#0014cc]/30 ring-offset-1' : ''
                } ${
                  column.locked
                    ? 'bg-slate-50 border-slate-200 cursor-not-allowed'
                    : draggedIndex === index
                      ? 'bg-[#0014cc]/5 border-[#0014cc]/40 shadow-md'
                      : dragOverIndex === index
                        ? 'bg-[#0014cc]/10 border-[#0014cc]/30'
                        : 'bg-white border-slate-200 hover:border-slate-300 cursor-move'
                }`}
              >
                {/* Drag handle */}
                <div
                  className={`flex-shrink-0 ${column.locked ? 'text-slate-300' : 'text-slate-400'}`}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 8h16M4 16h16"
                    />
                  </svg>
                </div>

                {/* Checkbox */}
                <div
                  onClick={() => !column.locked && toggleColumn(column.id)}
                  className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                    column.locked
                      ? 'bg-[#0014cc]/30 border-[#0014cc]/30 cursor-not-allowed'
                      : column.visible
                        ? 'bg-[#0014cc] border-[#0014cc] cursor-pointer'
                        : 'bg-white border-slate-300 cursor-pointer hover:border-slate-400'
                  }`}
                >
                  {column.visible && (
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                </div>

                {/* Label */}
                <div className="flex-1 min-w-0">
                  <span
                    className={`text-sm font-medium ${column.locked ? 'text-slate-400' : 'text-slate-700'}`}
                  >
                    {column.label}
                  </span>
                  {column.locked && (
                    <span className="ml-2 text-[10px] text-slate-400">(locked)</span>
                  )}
                </div>

                {/* Move up/down buttons */}
                {!column.locked && (
                  <div className="flex-shrink-0 flex gap-0.5">
                    <button
                      onClick={() => moveColumnUp(index)}
                      disabled={index === 0}
                      className={`p-0.5 rounded hover:bg-slate-100 ${
                        index === 0
                          ? 'text-slate-200 cursor-not-allowed'
                          : 'text-slate-400 hover:text-slate-600'
                      }`}
                      title="Move up"
                      aria-label={`Move ${column.label} up`}
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 15l7-7 7 7"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={() => moveColumnDown(index)}
                      disabled={index === localColumns.length - 1}
                      className={`p-0.5 rounded hover:bg-slate-100 ${
                        index === localColumns.length - 1
                          ? 'text-slate-200 cursor-not-allowed'
                          : 'text-slate-400 hover:text-slate-600'
                      }`}
                      title="Move down"
                      aria-label={`Move ${column.label} down`}
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-3 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg">
            <p className="text-xs text-slate-500">
              <span className="font-semibold text-slate-700">{visibleCount}</span> of{' '}
              {localColumns.length} columns visible
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-6 py-3 flex items-center justify-between flex-shrink-0">
          <button
            onClick={handleReset}
            className="text-xs font-medium text-slate-500 hover:text-slate-700 transition-colors"
          >
            Reset to Default
          </button>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm font-medium text-slate-600 bg-white hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-3 py-1.5 rounded-lg text-sm font-medium text-white bg-[#0014cc] hover:bg-[#0014cc]/90 transition-colors"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
